import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { InstallerLogo, installerBranding, appConfig } from "@/branding/Branding";
import { PASSWORD_INPUT_PROPS } from "@/utils/passwordInputProps";
import { homeForRole } from "@/components/ProtectedRoute";

/**
 * Landing / Auth — this is the WEB adaptation of `app/index.tsx` on the mobile app.
 *
 * Mobile layout the user asked us to match:
 *   ┌──────────────────────────────┐  ← navy gradient hero (rounded bottom corners)
 *   │   ┌───────────────┐          │
 *   │   │ Pearl Logo    │  white plate
 *   │   └───────────────┘          │
 *   │        Pearl                 │
 *   │  Your Solar Installation…    │
 *   │   Powered by EnergyFlow      │
 *   └──────────────────────────────┘
 *   ┌──────────────────────────────┐  ← white card floating up over hero
 *   │      [ Sign in ]              │
 *   │  email / password / …        │
 *   │  [ Sign in ]                 │
 *   │  ── Try the demo ──          │
 *   │  [ Customer ] [ Installer ]  │
 *   └──────────────────────────────┘
 *
 * On iPad-wide + desktop we render this composition in a wider container (max 640px)
 * with more vertical breathing room. The layout STAYS the same — this is intentional:
 * the mobile welcome pattern is the design; wide screens just get more air around it.
 */
export default function Landing() {
    // DEV-012B · Public registration entry point removed before Build 4.
    // Sign-in only. Forgot / reset password remain. New Customer accounts are
    // provisioned by Pearl Energy through the installer-side invite/onboarding
    // flow (DEV-011C — not yet delivered).
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState("");
    const [warning, setWarning] = useState("");
    const [busy, setBusy] = useState(false);

    if (user) {
        return <Navigate to={homeForRole(user.role)} replace />;
    }

    const submit = async (e) => {
        e?.preventDefault();
        setError("");
        setWarning("");
        setBusy(true);
        try {
            const u = await login(email, password);
            navigate(homeForRole(u.role));
        } catch (err) {
            const data = err.response?.data;
            setError(formatApiError(data?.detail) || err.message);
            // DEV-015C · Safe generic warning surfaced to both known
            // and unknown email paths — never reveals account state.
            if (data && typeof data.warning === "string" && data.warning) {
                setWarning(data.warning);
            }
        } finally {
            setBusy(false);
        }
    };

    // DEV-013 · Demo credential-fill handlers are inlined below inside
    // the guarded JSX branch so the sentinel strings (demo emails +
    // shared password) are entirely absent from a production bundle
    // (REACT_APP_ALLOW_DEMO_LOGIN unset ⇒ CRA replaces the branch with
    // a `false` constant and the minifier drops the JSX + strings).

    // DEV-013 · Demo helpers are hidden unless the build-time flag
    // REACT_APP_ALLOW_DEMO_LOGIN is explicitly set to "true" (preview /
    // dev only). Production builds never receive this flag, so the demo
    // box, credential-fill buttons and password123 constant do not
    // appear anywhere in the shipped bundle.
    const showDemoHelpers = process.env.REACT_APP_ALLOW_DEMO_LOGIN === "true";

    return (
        <div className="min-h-screen bg-[#F5F5F7]" data-testid="landing-page">
            {/* Hero — navy gradient, rounded bottom corners like the mobile welcome */}
            <section
                className="relative overflow-hidden rounded-b-[28px]"
                style={{ background: "linear-gradient(135deg, #131B2F 0%, #1F2A47 100%)" }}
            >
                {/* Subtle ambient light — kept faint so the navy stays dominant */}
                <div aria-hidden className="absolute inset-0 pointer-events-none">
                    <div className="absolute -top-40 -left-32 w-[520px] h-[520px] rounded-full opacity-40"
                        style={{ background: "radial-gradient(circle at 40% 40%, rgba(62,158,46,0.16), transparent 70%)" }} />
                    <div className="absolute -bottom-52 -right-24 w-[540px] h-[540px] rounded-full opacity-30"
                        style={{ background: "radial-gradient(circle at 60% 40%, rgba(255,255,255,0.06), transparent 65%)" }} />
                </div>

                <div className="relative z-10 mx-auto max-w-6xl px-8 md:px-14 pt-16 md:pt-24 pb-24 md:pb-32 flex flex-col items-center text-center animate-fade-up">
                    {/* White logo plate — mirrors mobile "logoPlate" */}
                    <div className="bg-white rounded-[20px] px-8 py-4 shadow-[0_8px_24px_rgba(0,0,0,0.20)]" data-testid="hero-logo-plate">
                        <InstallerLogo height={56} testid="login-installer-logo" />
                    </div>

                    <h1 className="mt-6 text-white text-3xl md:text-5xl font-medium tracking-tight" data-testid="hero-product">
                        {appConfig.name}
                    </h1>
                    <p className="mt-2 text-white/85 text-base md:text-lg" data-testid="hero-tagline">
                        {appConfig.tagline}
                    </p>
                    <p className="mt-1 text-white/55 text-sm tracking-[0.04em]" data-testid="hero-powered-by">
                        Powered by EnergyFlow
                    </p>
                </div>
            </section>

            {/* Auth card — floats up over the hero. Max 640px keeps the mobile composition
                intact even on iPad-wide and desktop, per the user's explicit request. */}
            <div className="mx-auto max-w-[640px] px-4 md:px-6 -mt-16 md:-mt-20 pb-16 relative z-20">
                <form
                    onSubmit={submit}
                    className="pe-card p-6 md:p-8"
                    data-testid="auth-card"
                >
                    {/* DEV-013B · Removed redundant "Sign in" header pill.
                        The green submit button already labels the action; a
                        second title above the fields was reintroduced in
                        Publish 11 and duplicated the CTA. */}

                    <label className="block text-sm text-[#4B5565] mb-1.5">Email</label>
                    <input
                        data-testid="auth-email-input"
                        type="email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="w-full h-11 rounded-md border border-[#E5E7EB] bg-white px-3.5 text-base text-[#131B2F] outline-none focus:border-[#3E9E2E] transition-colors"
                    />

                    <label className="block text-sm text-[#4B5565] mt-4 mb-1.5">
                        Password
                        <a
                            href="/forgot-password"
                            data-testid="auth-forgot-link"
                            className="float-right text-xs text-[#3E9E2E] hover:underline"
                        >
                            Forgot password?
                        </a>
                    </label>
                    <div className="relative">
                        <input
                            data-testid="auth-password-input"
                            type={showPassword ? "text" : "password"}
                            autoComplete="current-password"
                            {...PASSWORD_INPUT_PROPS}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                            className="w-full h-11 rounded-md border border-[#E5E7EB] bg-white px-3.5 pr-16 text-base text-[#131B2F] outline-none focus:border-[#3E9E2E] transition-colors"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword((v) => !v)}
                            data-testid="auth-password-toggle"
                            className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 text-xs text-[#4B5565] hover:text-[#131B2F]"
                            aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                            {showPassword ? "Hide" : "Show"}
                        </button>
                    </div>

                    {!!error && (
                        <div className="mt-4 text-sm text-[#E53E3E]" data-testid="auth-error">{error}</div>
                    )}
                    {!!warning && (
                        <div
                            role="alert"
                            data-testid="auth-warning"
                            className="mt-2 text-xs text-[#B7791F] bg-[#FFFAF0] border border-[#F6E05E] rounded-md px-3 py-2"
                        >
                            {warning}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="auth-submit-button"
                        className="mt-5 w-full h-12 rounded-md bg-[#3E9E2E] hover:bg-[#2E7E22] disabled:opacity-70 text-white text-base font-medium transition-colors flex items-center justify-center gap-2"
                    >
                        {busy ? (
                            <span className="flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-white animate-dot-1" />
                                <span className="w-1.5 h-1.5 rounded-full bg-white animate-dot-2" />
                                <span className="w-1.5 h-1.5 rounded-full bg-white animate-dot-3" />
                            </span>
                        ) : "Sign in"}
                    </button>

                    {/* Demo helpers — DEV-013 · dev/preview only, gated behind
                        REACT_APP_ALLOW_DEMO_LOGIN. Absent from production. */}
                    {showDemoHelpers && (
                    <div className="mt-6 p-3 rounded-md bg-[#F5F5F7] border border-[#E5E7EB]" data-testid="demo-box">
                        <div className="text-sm text-[#4B5565]">Try the demo</div>
                        <div className="mt-2 grid grid-cols-3 gap-2">
                            <button
                                type="button"
                                data-testid="demo-customer"
                                onClick={() => { setEmail("sarah@solarflow.demo"); setPassword("password123"); }}
                                className="py-2.5 rounded-md bg-white border border-[#E5E7EB] text-sm text-[#131B2F] font-medium hover:bg-[#F5F5F7] transition-colors"
                            >
                                Customer
                            </button>
                            <button
                                type="button"
                                data-testid="demo-installer"
                                onClick={() => { setEmail("engineer@solarflow.demo"); setPassword("password123"); }}
                                className="py-2.5 rounded-md bg-white border border-[#E5E7EB] text-sm text-[#131B2F] font-medium hover:bg-[#F5F5F7] transition-colors"
                            >
                                Engineer
                            </button>
                            <button
                                type="button"
                                data-testid="demo-operations"
                                onClick={() => { setEmail("ops@solarflow.demo"); setPassword("password123"); }}
                                className="py-2.5 rounded-md bg-white border border-[#E5E7EB] text-sm text-[#131B2F] font-medium hover:bg-[#F5F5F7] transition-colors"
                            >
                                Operations
                            </button>
                        </div>
                    </div>
                    )}
                </form>

                <div className="mt-8 text-center text-xs text-[#4B5565]" data-testid="login-copyright-footer">
                    <div>Pearl - EnergyFlow v1.0 · Web Portal · Powered by EnergyFlow</div>
                    <div className="mt-1">© {new Date().getFullYear()} {installerBranding.name}</div>
                </div>
            </div>
        </div>
    );
}
